// Onglet de campagne « Importer depuis DossierFacile » (F-036) : import d'un dossier partagé par le candidat.
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Button, ButtonLink, Card, Checkbox, Empty, ErrorBox, Input, Loading, Notice, SimBadge, StatusChip, Table, fieldError } from "@core/ui";
import { APPLICATION_STATUS, useAction } from "./shared";

type ImportRow = {
  id: string;
  application_id: string | null;
  household_label: string | null;
  application_status: string | null;
  df_dossier_id: string;
  df_status: string;
  df_status_on: string | null;
  link_hint: string;
  mode: string;
  persons_count: number;
  documents_count: number;
  status: "importe" | "partiel";
  error: string | null;
  created_by_name: string | null;
  created_at: string;
};

const DF_STATUS: Record<string, string> = {
  VALIDATED: "Déclaré vérifié par DossierFacile",
  TO_PROCESS: "En cours d’instruction",
  INCOMPLETE: "Incomplet",
  DECLINED: "Non validé",
  ARCHIVED: "Archivé",
};

export function DossierFacileTab({ orgId, campaignId }: { orgId: string; campaignId: string }) {
  const org = useOrg();
  const url = `/api/v1/orgs/${orgId}/campaigns/${campaignId}/dossierfacile-imports`;
  const q = useGet<{ adapter: string; mode: string; items: ImportRow[] }>(url);
  const action = useAction([url, `/api/v1/orgs/${orgId}/campaigns/${campaignId}`, `/api/v1/orgs/${orgId}/applications`]);
  const [link, setLink] = useState("");
  const [consent, setConsent] = useState(false);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  return (
    <div className="stack">
      <Card
        title={
          <span className="row">
            Importer depuis DossierFacile <SimBadge mode={v.mode} />
          </span>
        }
      >
        <p className="muted">
          Le candidat vous transmet le lien de partage de son dossier DossierFacile. L’import crée une candidature dans cette campagne avec les personnes et les pièces du dossier, en conservant leur provenance. Le statut de DossierFacile est une information, pas un certificat : les contrôles de Bailivo restent à mener sur les pièces importées.
        </p>
        <Notice>La vérification de DossierFacile est gratuite : l’import n’est pas décompté de votre offre.</Notice>
        {v.mode === "simulateur" ? (
          <Notice tone="simulateur" title="Mode simulateur">
            L’accès partenaire DossierFacile n’est pas encore obtenu : les dossiers importés sont fictifs et générés localement. Aucune donnée réelle n’est lue.
          </Notice>
        ) : null}
        {org.can("operate") ? (
          <form
            className="stack-sm"
            onSubmit={async (e) => {
              e.preventDefault();
              const ok = await action.run("importer", () => post(url, { share_link: link, candidate_consent: consent }), "Dossier importé dans la campagne.");
              if (ok) {
                setLink("");
                setConsent(false);
              }
            }}
          >
            <Input
              label="Lien de partage DossierFacile"
              placeholder="https://locataire.dossierfacile.logement.gouv.fr/file/…"
              required
              value={link}
              error={fieldError(action.error, "share_link")}
              onChange={(e) => setLink(e.target.value)}
            />
            <Checkbox label="Le candidat m’a lui-même transmis ce lien pour cette location." checked={consent} onChange={setConsent} />
            {fieldError(action.error, "candidate_consent") ? (
              <p className="bv-champ-aide decision-erreur" role="alert">
                {fieldError(action.error, "candidate_consent")}
              </p>
            ) : null}
            <div className="form-actions">
              <Button type="submit" busy={action.busy === "importer"}>
                Importer le dossier
              </Button>
            </div>
            {action.error && !fieldError(action.error, "share_link") && !fieldError(action.error, "candidate_consent") ? <ErrorBox error={action.error} /> : null}
          </form>
        ) : null}
      </Card>
      <Card title="Dossiers importés">
        {v.items.length ? (
          <Table caption="Dossiers importés depuis DossierFacile">
            <thead>
              <tr>
                <th>Candidature</th>
                <th>Statut DossierFacile</th>
                <th>Contenu</th>
                <th>Import</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {v.items.map((i) => (
                <tr key={i.id}>
                  <td>
                    {i.household_label ?? "—"}
                    <span className="block small muted">{i.application_status ? APPLICATION_STATUS[i.application_status] ?? i.application_status : ""}</span>
                  </td>
                  <td>
                    {DF_STATUS[i.df_status] ?? i.df_status}
                    <span className="block small muted">
                      au {date(i.df_status_on)} · <SimBadge mode={i.mode} />
                    </span>
                  </td>
                  <td>
                    {i.persons_count} personne(s), {i.documents_count} pièce(s)
                    {i.status === "partiel" ? (
                      <span className="block">
                        <StatusChip status="non_concluant" label="Import partiel" />
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {dateTime(i.created_at)}
                    <span className="block small muted">
                      lien {i.link_hint}
                      {i.created_by_name ? ` · ${i.created_by_name}` : ""}
                    </span>
                  </td>
                  <td className="right">
                    {i.application_id ? (
                      <ButtonLink to={org.to(`candidatures/${i.application_id}`)} variant="discret" small>
                        Ouvrir
                      </ButtonLink>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Empty title="Aucun dossier importé">Collez le lien transmis par un candidat pour l’ajouter à cette campagne.</Empty>
        )}
      </Card>
    </div>
  );
}
